/**
 * DetectionFocusView — an alternative presentation of the same `detections`
 * array the grid renders.  One detection is shown large (the full DetectionCard,
 * overlays and all), with a cheap thumbnail strip of the whole day underneath.
 *
 * The strip deliberately does NOT mount CoordinateOverlay or the evaluation
 * controls: with ~100 detections per day that machinery is the expensive part,
 * and a thumbnail only needs one <img>.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { DetectionCard, type Detection } from "./DetectionCard";
import { theme, tint } from "../theme";
import { SIDEBAR_WIDTH, MOBILE_BP } from "./Sidebar";

const THUMB_SIZE = 84;
// Thumb + the strip's 8px vertical padding + its top border — the height the
// page has to keep free under the viewport-pinned strip.
// Thumbnails + their padding + the hint row above them + the panel's bottom
// margin. The stage measures against this, so it has to match what the panel
// actually occupies or the card runs under it again.
const STRIP_HEIGHT = THUMB_SIZE + 16 + 33 + 16;

interface Props {
  detections:          Detection[];
  showMarkers:         boolean;
  onEvaluationChange?: (id: number, evaluation: string | null) => void;
  onDelete?:           (id: number) => void;
}

function hhmm(ts: string): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const isSafety = (d: Detection) =>
  d.detection_events?.some((e) => e.type_name.toLowerCase().includes("helmet")) ?? false;

// ── Thumbnail ────────────────────────────────────────────────────────────────
// Plain <img> + a timestamp chip. No overlay, no eval buttons, no hover state
// machinery — this is rendered once per detection on the day.

interface ThumbProps {
  detection: Detection;
  active:    boolean;
  onSelect:  () => void;
}

function DetectionThumb({ detection, active, onSelect }: ThumbProps) {
  const url = detection.image_after_url ?? detection.image_before_url;
  const accent = isSafety(detection) ? theme.warning : theme.danger;

  return (
    <button
      data-active={active ? "true" : undefined}
      onClick={onSelect}
      title={`${hhmm(detection.timestamp)} — ${
        detection.detection_events?.map((e) => e.type_name).join(", ") || "—"
      }`}
      style={{
        position: "relative",
        flex: `0 0 ${THUMB_SIZE}px`,
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        padding: 0,
        borderRadius: theme.radius,
        overflow: "hidden",
        cursor: "pointer",
        background: theme.bgBase,
        border: `2px solid ${active ? theme.gold : theme.border}`,
        boxShadow: active ? `0 0 0 2px ${tint(theme.gold, 27)}` : "none",
        opacity: active ? 1 : 0.72,
        transition: "opacity 0.15s, border-color 0.15s, box-shadow 0.15s",
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
        />
      ) : (
        <span style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: "100%", height: "100%", fontSize: 10, color: theme.textMuted,
        }}>
          …
        </span>
      )}
      {/* Category tint stripe — keeps Safety vs Disappearance readable at a glance */}
      <span style={{
        position: "absolute", left: 0, right: 0, top: 0,
        height: 3, background: accent,
      }} />
      <span style={{
        position: "absolute", left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.55)",
        color: theme.onDark,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
        padding: "1px 0",
      }}>
        {hhmm(detection.timestamp)}
      </span>
    </button>
  );
}

// ── Focus view ───────────────────────────────────────────────────────────────

export function DetectionFocusView({ detections, showMarkers, onEvaluationChange, onDelete }: Props) {
  // Track the active detection by id so live WS inserts/removals don't shift
  // the stage out from under the user; fall back to the last index we were on.
  const [activeId, setActiveId] = useState<number | null>(null);
  const idxRef = useRef(0);

  const foundIdx    = detections.findIndex((d) => d.id === activeId);
  const activeIndex = foundIdx >= 0
    ? foundIdx
    : Math.min(idxRef.current, Math.max(0, detections.length - 1));
  const active = detections[activeIndex];

  useEffect(() => { idxRef.current = activeIndex; }, [activeIndex]);
  useEffect(() => {
    if (active && active.id !== activeId) setActiveId(active.id);
    if (!active && activeId !== null) setActiveId(null);
  }, [active, activeId]);

  const step = useCallback((delta: 1 | -1) => {
    const next = detections[idxRef.current + delta];
    if (next) setActiveId(next.id);
  }, [detections]);

  // Keyboard navigation — ignored while typing in a field.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
      if (e.key === "ArrowLeft")  step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step]);

  // Keep the active thumbnail visible in the strip.
  const stripRef = useRef<HTMLDivElement>(null);
  // The filmstrip panel, so the stage can measure against where it actually
  // sits rather than against a reconstruction of its height.
  const panelRef = useRef<HTMLDivElement>(null);

  // How tall the stage may be: whatever is left between wherever it starts on
  // the page and the pinned filmstrip.
  //
  // Measured, not derived from a constant — everything above (month strip,
  // timeline, header, filter row) reflows with the window and with the data, so
  // any fixed offset is wrong at some width. But measuring ONCE on mount was
  // worse than a constant: the rows above render after their fetches resolve,
  // so the first measurement saw the stage at the top of an empty page, read a
  // height nearly a viewport too large, and never corrected itself — which is
  // how the card ended up taller than its container and slid under the strip.
  //
  // A ResizeObserver on the scroll container catches every one of those
  // reflows. It only ever observes an ancestor, never the stage itself, so
  // resizing the stage cannot feed back into the observer.
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageHeight, setStageHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // Before the page above has laid out, `top` is 0 and the answer would be
      // a whole viewport too generous. Wait for a real position instead of
      // committing to a wrong one.
      if (top <= 0) return;
      // Measure to the panel's real top edge. Deriving it from STRIP_HEIGHT
      // meant re-deriving a number the layout already knows, and every part the
      // constant missed — here the page's own bottom padding — became a sliver
      // of scroll on a page that otherwise fits exactly.
      const panelTop = panelRef.current?.getBoundingClientRect().top;
      const floor = panelTop && panelTop > top ? panelTop : window.innerHeight - STRIP_HEIGHT;
      setStageHeight(Math.max(280, floor - top - 14));
    };

    measure();
    const raf = requestAnimationFrame(measure);

    const observer = new ResizeObserver(measure);
    const above = stageRef.current?.parentElement?.parentElement;
    if (above) observer.observe(above);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [detections.length]);

  // Everything on the card that is NOT the image row: the badge line, the
  // cause/time line and the verdict buttons. Measured from the rendered card,
  // because it changes with the badges a detection happens to carry — a
  // duplicate with three badges is taller than a plain one, and a fixed
  // allowance that fits the plain case overflows the other.
  const cardBoxRef = useRef<HTMLDivElement>(null);
  const [cardChrome, setCardChrome] = useState(150);
  useLayoutEffect(() => {
    const measure = () => {
      const card = cardBoxRef.current?.firstElementChild as HTMLElement | undefined;
      const img  = card?.querySelector("img");
      if (!card || !img || !img.clientHeight) return;
      const chrome = card.getBoundingClientRect().height - img.getBoundingClientRect().height;
      if (chrome > 0) setCardChrome(Math.ceil(chrome));
    };
    measure();
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [active?.id, stageHeight]);

  const isNarrow = typeof window !== "undefined" && window.innerWidth < MOBILE_BP;
  useEffect(() => {
    const el = stripRef.current?.querySelector<HTMLElement>("[data-active='true']");
    el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [activeIndex, detections.length]);

  if (!active) return null;

  const navBtn = (enabled: boolean): React.CSSProperties => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
    width: 38, height: 38,
    borderRadius: theme.radius,
    border: `1px solid ${enabled ? theme.border : "transparent"}`,
    background: "transparent",
    color: enabled ? theme.textSecondary : theme.textMuted,
    fontSize: 20, lineHeight: 1, padding: 0,
    cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.3,
    transition: "opacity 0.15s, border-color 0.15s",
  });

  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < detections.length - 1;

  return (
    // Reserve room for the viewport-pinned filmstrip so the last of the content
    // can still be scrolled clear of it.
    // No bottom padding: the stage is already sized to stop above the panel, so
    // reserving the strip's height a second time pushed the document past the
    // viewport and left a scroll with nothing in it.
    <div>
      {/* ── Center stage ────────────────────────────────────────────────── */}
      <div
        ref={stageRef}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 14,
          height: stageHeight ?? undefined,
        }}
      >
        <button
          onClick={() => step(-1)}
          disabled={!hasPrev}
          title="Previous detection (←)"
          style={navBtn(hasPrev)}
        >‹</button>

        <div ref={cardBoxRef} style={{
          flex: "1 1 auto", minWidth: 0,
          // Two square panes side by side, so the card's width is about twice
          // its image height, plus the meta rows below. cardChrome is measured
          // from the rendered card rather than guessed: a guess that is too
          // small produces a card taller than the stage, which is exactly how
          // this slid under the filmstrip.
          maxWidth: stageHeight
            ? Math.max(420, (stageHeight - cardChrome) * 2)
            : 820,
          display: "flex", flexDirection: "column", justifyContent: "center",
        }}>
          <DetectionCard
            key={active.id}
            detection={active}
            showMarkers={showMarkers}
            onEvaluationChange={onEvaluationChange}
            onDelete={onDelete}
          />
        </div>

        <button
          onClick={() => step(1)}
          disabled={!hasNext}
          title="Next detection (→)"
          style={navBtn(hasNext)}
        >›</button>
      </div>

      {/* ── Filmstrip ───────────────────────────────────────────────────── */}
      {/* Pinned to the viewport, not merely sticky: the strip is the primary way
          to move between detections, so it has to stay reachable no matter how
          far the page is scrolled. The container above reserves its height. */}
      {/* An inset, rounded panel rather than a full-bleed bar, matching the
          month strip and timeline at the top of the page. The position and the
          keyboard hint sit inside it, above the thumbnails, so the two read as
          one control the way those two do. Still fixed to the viewport: the
          strip is the primary way to move between detections, so it has to stay
          reachable however far the page is scrolled. */}
      <div ref={panelRef} style={{
        position: "fixed",
        // Starts at the rail's edge, not at 0. This bar predates the sidebar,
        // and a viewport-wide fixed element slides underneath it and hides the
        // navigation. The rail is not rendered below MOBILE_BP, so the offset
        // has to disappear with it.
        left: isNarrow ? 12 : SIDEBAR_WIDTH + 28,
        right: isNarrow ? 12 : 28,
        bottom: 16,
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        overflow: "hidden",
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        zIndex: 20,
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 10, flexWrap: "wrap",
          padding: "7px 14px",
          borderBottom: `1px solid ${theme.border}`,
        }}>
          <span style={{ color: theme.textSecondary, fontSize: theme.fontSizeXs, fontWeight: 700 }}>
            {activeIndex + 1} / {detections.length}
          </span>
          <span style={{ color: theme.textMuted, fontSize: theme.fontSize2Xs }}>
            Keyboard: <b>←</b> / <b>→</b> switch detection · click a thumbnail to jump
          </span>
        </div>
        <div style={{ padding: "8px 12px" }}>
        <div
          ref={stripRef}
          className="no-scrollbar"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            overflowX: "auto",
            scrollbarWidth: "none",
            paddingBottom: 2,
          }}
        >
          {detections.map((d) => (
            <DetectionThumb
              key={d.id}
              detection={d}
              active={d.id === active.id}
              onSelect={() => setActiveId(d.id)}
            />
          ))}
        </div>
        </div>
      </div>
    </div>
  );
}
